import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import SARSymbol from '../components/SARSymbol';
import {
  Check,
  X,
  ArrowRight,
  Shield,
  Zap,
  DollarSign,
  Users,
  Globe,
  Headphones,
  Award,
  TrendingUp,
  Clock,
  Building2,
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ComparisonPage = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [bundlePlans, setBundlePlans] = useState([]);
  const [currencies, setCurrencies] = useState({ available: [], symbols: {} });
  const [selectedCurrency, setSelectedCurrency] = useState('usd');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);

  useEffect(() => {
    const fetchBundles = async () => {
      try {
        const res = await fetch(`${API_URL}/api/subscriptions/bundles`);
        const data = await res.json();
        setBundlePlans(data.bundles || []);
        setCurrencies(data.currencies || { available: [], symbols: {} });
      } catch (err) {
        console.error('Failed to load bundles:', err);
      }
    };
    fetchBundles();
  }, []);

  // Get price based on selected currency
  const getPrice = (bundle, cycle = 'yearly') => {
    const priceKey = `price_${selectedCurrency}_${cycle}`;
    return bundle[priceKey] || 0;
  };

  // Format price with currency symbol
  const formatPrice = (amount) => {
    if (selectedCurrency === 'sar') {
      return amount.toLocaleString(); // Symbol will be rendered separately
    }
    const symbol = currencies.symbols[selectedCurrency] || '$';
    return `${symbol}${amount.toLocaleString()}`;
  };

  // Render price with SAR symbol support
  const renderPrice = (amount, suffix = '') => {
    if (selectedCurrency === 'sar') {
      return (
        <span className="inline-flex items-center gap-1">
          <SARSymbol size={14} />
          {amount.toLocaleString()}{suffix}
        </span>
      );
    }
    const symbol = currencies.symbols[selectedCurrency] || '$';
    return `${symbol}${amount.toLocaleString()}${suffix}`;
  };

  // Currency display names
  const currencyNames = {
    usd: 'USD ($)',
    eur: 'EUR (€)',
    gbp: 'GBP (£)',
    sar: 'SAR (ر.س)',
    aed: 'AED (د.إ)',
    kwd: 'KWD (د.ك)',
    bhd: 'BHD (د.ب)',
    omr: 'OMR (ر.ع)',
    qar: 'QAR (ر.ق)'
  };

  const comparisonFeatures = [
    {
      category: isRTL ? 'الفواتير والمحاسبة' : 'Invoicing & Accounting',
      features: [
        { name: isRTL ? 'فواتير غير محدودة' : 'Unlimited Invoices', flowfeed: true, others: 'Limited' },
        { name: isRTL ? 'الفوترة المتكررة' : 'Recurring Billing', flowfeed: true, others: true },
        { name: isRTL ? 'متوافق مع الضرائب العالمية' : 'Global Tax Compliant (VAT/GST)', flowfeed: true, others: true },
        { name: isRTL ? 'الفوترة الإلكترونية' : 'E-Invoicing (ZATCA, PEPPOL)', flowfeed: 'Native', others: 'Add-on $$$' },
        { name: isRTL ? 'دليل الحسابات' : 'Chart of Accounts', flowfeed: true, others: 'Premium' },
        { name: isRTL ? 'قيود اليومية' : 'Journal Entries', flowfeed: true, others: 'Premium' },
        { name: isRTL ? 'التسوية البنكية' : 'Bank Reconciliation', flowfeed: true, others: 'Premium' },
      ]
    },
    {
      category: isRTL ? 'إدارة العملاء' : 'Customer Management',
      features: [
        { name: isRTL ? 'إدارة شركاء الأعمال' : 'Business Partners Management', flowfeed: true, others: 'Separate Product' },
        { name: isRTL ? 'نظام إدارة العملاء الكامل' : 'Full CRM System', flowfeed: 'Included', others: 'Extra Cost' },
        { name: isRTL ? 'بوابة العملاء' : 'Customer Portal', flowfeed: true, others: true },
        { name: isRTL ? 'تتبع العملاء المحتملين' : 'Lead Tracking', flowfeed: true, others: 'Separate Product' },
      ]
    },
    {
      category: isRTL ? 'الميزات المتقدمة' : 'Advanced Features',
      features: [
        { name: isRTL ? 'دعم متعدد العملات' : 'Multi-Currency Support', flowfeed: true, others: true },
        { name: isRTL ? 'إدارة المخزون' : 'Inventory Management', flowfeed: 'Included', others: 'Separate Product' },
        { name: isRTL ? 'إدارة الموارد البشرية والرواتب' : 'HR & Payroll', flowfeed: 'Included', others: 'Separate Product' },
        { name: isRTL ? 'نقاط البيع' : 'POS System', flowfeed: 'Included', others: 'Separate Product' },
        { name: isRTL ? 'إدارة المشاريع' : 'Project Management', flowfeed: 'Included', others: 'Separate Product' },
      ]
    },
    {
      category: isRTL ? 'الدعم والتوطين' : 'Support & Localization',
      features: [
        { name: isRTL ? 'دعم متعدد اللغات' : 'Multi-Language Support', flowfeed: true, others: 'Partial' },
        { name: isRTL ? 'واجهة RTL للعربية' : 'Arabic RTL Interface', flowfeed: true, others: 'Partial' },
        { name: isRTL ? 'دعم 24/7' : '24/7 Support', flowfeed: true, others: 'Premium Only' },
        { name: isRTL ? 'الترقية لنظام ERP كامل' : 'Upgrade to Full ERP', flowfeed: 'Seamless', others: 'Multiple Products' },
      ]
    }
  ];

  const advantages = [
    {
      icon: DollarSign,
      title: isRTL ? 'توفير يصل إلى 60%' : 'Save Up to 60%',
      description: isRTL 
        ? 'أسعار تنافسية بدون رسوم خفية أو إضافات مكلفة'
        : 'Competitive pricing without hidden fees or expensive add-ons'
    },
    {
      icon: Shield,
      title: isRTL ? 'الامتثال الضريبي العالمي' : 'Global Tax Compliance',
      description: isRTL
        ? 'متوافق مع VAT وZATCA وGST ومتطلبات الضرائب العالمية'
        : 'VAT, ZATCA, GST and global tax requirements built-in'
    },
    {
      icon: Globe,
      title: isRTL ? 'دعم متعدد اللغات' : 'Multi-Language Support',
      description: isRTL
        ? 'دعم كامل للعربية والإنجليزية مع واجهة RTL'
        : 'Full Arabic, English support with RTL interface'
    },
    {
      icon: TrendingUp,
      title: isRTL ? 'نمو سلس' : 'Seamless Growth',
      description: isRTL
        ? 'ابدأ بالفواتير، توسع لنظام ERP كامل بنفس المنصة'
        : 'Start with billing, expand to full ERP on the same platform'
    },
    {
      icon: Users,
      title: isRTL ? 'مستخدمين مشمولين' : 'Users Included',
      description: isRTL
        ? '3-25 مستخدم مشمول حسب الخطة، بدون رسوم لكل مستخدم'
        : '3-25 users included per plan, no per-seat pricing trap'
    },
    {
      icon: Zap,
      title: isRTL ? 'منصة واحدة' : 'One Platform',
      description: isRTL
        ? 'كل شيء في مكان واحد - لا حاجة لمنتجات متعددة'
        : 'Everything in one place - no need for multiple products'
    }
  ];

  const renderFeatureValue = (value) => {
    if (value === true) {
      return <Check className="h-5 w-5 text-emerald-500" />;
    } else if (value === false) {
      return <X className="h-5 w-5 text-red-400" />;
    } else {
      return <span className="text-sm font-medium">{value}</span>;
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="comparison-page">
      {/* SEO Title */}
      <Helmet>
        <title>Why FlowFeed? POS Comparison | FlowFeed POS</title>
      </Helmet>
      {/* Navigation */}
      <nav className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/favicon.svg" alt="FlowFeed ERP" className="h-8 w-8" />
            <span className="font-bold text-xl">FlowFeed ERP</span>
          </Link>
          <div className="flex items-center gap-4">
            {/* Currency Selector */}
            <div className="relative" data-testid="currency-selector">
              <button
                onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card hover:bg-muted transition text-sm font-medium"
              >
                <Globe className="h-4 w-4" />
                {selectedCurrency.toUpperCase()}
                <ChevronDown className={`h-4 w-4 transition ${showCurrencyDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showCurrencyDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-xl shadow-xl z-50 py-2 max-h-64 overflow-y-auto">
                  {currencies.available.map((curr) => (
                    <button
                      key={curr}
                      onClick={() => {
                        setSelectedCurrency(curr);
                        setShowCurrencyDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-muted transition ${
                        selectedCurrency === curr ? 'bg-muted font-medium' : ''
                      }`}
                    >
                      {currencyNames[curr] || curr.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <Link to="/blog" className="text-muted-foreground hover:text-foreground transition">
              {isRTL ? 'المدونة' : 'Blog'}
            </Link>
            <Link 
              to="/signup" 
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:opacity-90 transition"
            >
              {isRTL ? 'ابدأ مجاناً' : 'Start Free'}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 via-purple-500/5 to-transparent"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-flex items-center gap-2 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
              <Award className="h-4 w-4" />
              {isRTL ? 'الخيار الأفضل للأعمال حول العالم' : 'The Smart Choice for Businesses Worldwide'}
            </span>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              <span className="text-foreground">{isRTL ? 'لماذا' : 'Why'} </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-emerald-500">
                FlowFeed ERP
              </span>
              <span className="text-foreground"> {isRTL ? '؟' : '?'}</span>
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              {isRTL 
                ? 'اكتشف كيف يقدم FlowFeed ERP قيمة أفضل مقارنة بالحلول التقليدية - بتكلفة أقل وميزات أكثر'
                : 'See how FlowFeed ERP delivers better value compared to traditional solutions - at a lower cost with more features'}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold hover:opacity-90 transition shadow-lg"
              >
                {isRTL ? 'جرب مجاناً لمدة 7 أيام' : 'Try Free for 7 Days'}
                <ArrowRight className={`h-5 w-5 ${isRTL ? 'rotate-180' : ''}`} />
              </Link>
              <Link
                to="/#pricing"
                className="inline-flex items-center justify-center gap-2 border border-border bg-card/50 px-8 py-4 rounded-xl font-semibold hover:bg-card transition"
              >
                {isRTL ? 'عرض الأسعار' : 'View Pricing'}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Advantages */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {isRTL ? 'مزايا FlowFeed الرئيسية' : 'Key FlowFeed Advantages'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {isRTL 
                ? 'ما يجعلنا الخيار الأفضل للشركات حول العالم'
                : 'What makes us the better choice for businesses worldwide'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {advantages.map((adv, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition"
              >
                <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center mb-4">
                  <adv.icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{adv.title}</h3>
                <p className="text-sm text-muted-foreground">{adv.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {isRTL ? 'مقارنة الميزات' : 'Feature Comparison'}
            </h2>
            <p className="text-muted-foreground">
              {isRTL 
                ? 'قارن FlowFeed ERP مع الحلول الأخرى في السوق'
                : 'Compare FlowFeed ERP with other solutions in the market'}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              {/* Table Header */}
              <div className="grid grid-cols-3 bg-muted/50 border-b border-border">
                <div className="p-4 font-semibold">{isRTL ? 'الميزة' : 'Feature'}</div>
                <div className="p-4 font-semibold text-center bg-gradient-to-r from-blue-500/10 to-purple-500/10">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                    FlowFeed ERP
                  </span>
                </div>
                <div className="p-4 font-semibold text-center text-muted-foreground">
                  {isRTL ? 'الحلول الأخرى' : 'Other Solutions'}
                </div>
              </div>

              {/* Table Body */}
              {comparisonFeatures.map((category, catIdx) => (
                <div key={catIdx}>
                  {/* Category Header */}
                  <div className="grid grid-cols-3 bg-muted/30 border-b border-border">
                    <div className="col-span-3 p-3 font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                      {category.category}
                    </div>
                  </div>
                  
                  {/* Features */}
                  {category.features.map((feature, featIdx) => (
                    <div 
                      key={featIdx} 
                      className={`grid grid-cols-3 border-b border-border/50 hover:bg-muted/20 transition ${
                        featIdx === category.features.length - 1 ? 'border-b-border' : ''
                      }`}
                    >
                      <div className="p-4 text-sm">{feature.name}</div>
                      <div className="p-4 flex justify-center items-center bg-gradient-to-r from-blue-500/5 to-purple-500/5">
                        {renderFeatureValue(feature.flowfeed)}
                      </div>
                      <div className="p-4 flex justify-center items-center text-muted-foreground">
                        {renderFeatureValue(feature.others)}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Comparison */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {isRTL ? 'مقارنة التكلفة السنوية' : 'Annual Cost Comparison'}
            </h2>
            <p className="text-muted-foreground mb-6">
              {isRTL ? 'وفر أكثر مع FlowFeed ERP' : 'Save more with FlowFeed ERP'}
            </p>
            
            {/* Currency Selector for Pricing Section */}
            <div className="flex justify-center gap-2 flex-wrap">
              {currencies.available.slice(0, 5).map((curr) => (
                <button
                  key={curr}
                  onClick={() => setSelectedCurrency(curr)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-1 ${
                    selectedCurrency === curr 
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' 
                      : 'bg-card border border-border hover:bg-muted'
                  }`}
                >
                  {curr === 'sar' ? <SARSymbol size={14} /> : null}
                  {curr.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Scenario 1: Small Business - Starter */}
              {bundlePlans.find(b => b.id === 'starter') && (
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="text-center mb-6">
                    <Building2 className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                    <h3 className="font-semibold text-lg">{isRTL ? 'شركة صغيرة' : 'Small Business'}</h3>
                    <p className="text-sm text-muted-foreground">
                      {bundlePlans.find(b => b.id === 'starter')?.users_included || 3} {isRTL ? 'مستخدمين' : 'Users'}
                    </p>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-emerald-500/10 rounded-lg">
                      <span className="font-medium text-emerald-600 dark:text-emerald-400">FlowFeed</span>
                      <span className="font-bold">
                        {renderPrice(getPrice(bundlePlans.find(b => b.id === 'starter'), 'yearly'), `/${isRTL ? 'سنة' : 'yr'}`)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                      <span className="text-muted-foreground">{isRTL ? 'الحلول الأخرى' : 'Others'}</span>
                      <span className="text-muted-foreground">
                        ~{renderPrice(Math.round(getPrice(bundlePlans.find(b => b.id === 'starter'), 'yearly') * 2.5), `/${isRTL ? 'سنة' : 'yr'}`)}
                      </span>
                    </div>
                    <div className="text-center pt-2">
                      <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                        {isRTL ? 'توفير 60%' : 'Save 60%'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Scenario 2: Growing Business - Business Plan */}
              {bundlePlans.find(b => b.id === 'business') && (
                <div className="bg-card border-2 border-purple-500/50 rounded-xl p-6 shadow-lg relative">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                      {isRTL ? 'الأكثر شعبية' : 'MOST POPULAR'}
                    </span>
                  </div>
                  <div className="text-center mb-6">
                    <TrendingUp className="h-10 w-10 mx-auto mb-3 text-purple-500" />
                    <h3 className="font-semibold text-lg">{isRTL ? 'شركة نامية' : 'Growing Business'}</h3>
                    <p className="text-sm text-muted-foreground">
                      {bundlePlans.find(b => b.id === 'business')?.users_included || 10} {isRTL ? 'مستخدمين' : 'Users'}
                    </p>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-emerald-500/10 rounded-lg">
                      <span className="font-medium text-emerald-600 dark:text-emerald-400">FlowFeed</span>
                      <span className="font-bold">
                        {renderPrice(getPrice(bundlePlans.find(b => b.id === 'business'), 'yearly'), `/${isRTL ? 'سنة' : 'yr'}`)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                      <span className="text-muted-foreground">{isRTL ? 'الحلول الأخرى' : 'Others'}</span>
                      <span className="text-muted-foreground">
                        ~{renderPrice(Math.round(getPrice(bundlePlans.find(b => b.id === 'business'), 'yearly') * 2.7), `/${isRTL ? 'سنة' : 'yr'}`)}
                      </span>
                    </div>
                    <div className="text-center pt-2">
                      <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                        {isRTL ? 'توفير 63%' : 'Save 63%'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Scenario 3: Enterprise */}
              {bundlePlans.find(b => b.id === 'enterprise') && (
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="text-center mb-6">
                    <Users className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                    <h3 className="font-semibold text-lg">{isRTL ? 'مؤسسة' : 'Enterprise'}</h3>
                    <p className="text-sm text-muted-foreground">
                      {bundlePlans.find(b => b.id === 'enterprise')?.users_included || 25} {isRTL ? 'مستخدم' : 'Users'}
                    </p>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-emerald-500/10 rounded-lg">
                      <span className="font-medium text-emerald-600 dark:text-emerald-400">FlowFeed</span>
                      <span className="font-bold">
                        {renderPrice(getPrice(bundlePlans.find(b => b.id === 'enterprise'), 'yearly'), `/${isRTL ? 'سنة' : 'yr'}`)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                      <span className="text-muted-foreground">{isRTL ? 'الحلول الأخرى' : 'Others'}</span>
                      <span className="text-muted-foreground">
                        ~{renderPrice(Math.round(getPrice(bundlePlans.find(b => b.id === 'enterprise'), 'yearly') * 2.4), `/${isRTL ? 'سنة' : 'yr'}`)}
                      </span>
                    </div>
                    <div className="text-center pt-2">
                      <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                        {isRTL ? 'توفير 58%' : 'Save 58%'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <p className="text-center text-sm text-muted-foreground mt-8">
              * {isRTL 
                ? 'المقارنة تستند إلى الأسعار المعلنة للحلول المماثلة في السوق العالمي'
                : 'Comparison based on published prices of similar solutions in the global market'}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-3xl p-12 relative overflow-hidden">
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:30px_30px]"></div>
              
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  {isRTL ? 'هل أنت مستعد للتوفير؟' : 'Ready to Save?'}
                </h2>
                <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
                  {isRTL 
                    ? 'انضم لآلاف الشركات حول العالم التي اختارت FlowFeed ERP'
                    : 'Join thousands of businesses worldwide who chose FlowFeed ERP'}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/signup"
                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white px-8 py-4 rounded-xl font-semibold hover:opacity-90 transition"
                  >
                    {isRTL ? 'ابدأ تجربتك المجانية' : 'Start Your Free Trial'}
                    <ChevronRight className={`h-5 w-5 ${isRTL ? 'rotate-180' : ''}`} />
                  </Link>
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/10 transition"
                  >
                    <Headphones className="h-5 w-5" />
                    {isRTL ? 'تحدث مع المبيعات' : 'Talk to Sales'}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-muted/50 border-t border-border py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img src="/favicon.svg" alt="FlowFeed ERP" className="h-8 w-8" />
              <span className="font-bold text-xl">FlowFeed ERP</span>
            </div>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground transition">{isRTL ? 'الرئيسية' : 'Home'}</Link>
              <Link to="/blog" className="hover:text-foreground transition">{isRTL ? 'المدونة' : 'Blog'}</Link>
              <Link to="/" className="hover:text-foreground transition">{isRTL ? 'اتصل بنا' : 'Contact'}</Link>
            </div>
          </div>
          <div className="border-t border-border mt-8 pt-8 text-center text-sm text-muted-foreground">
            © 2025 FlowFeed ERP Solutions. {isRTL ? 'جميع الحقوق محفوظة' : 'All rights reserved'}.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ComparisonPage;
